import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import classNames from "classnames";
import { Table } from "react-bootstrap";

import { useGamesContext } from "../contexts/Games";
import { dateFormat } from "../lib/dateFormat";
import { SortColumns } from "../types/games";

import { currencyFormat, defaultTimeFormat } from "../../../../lib/utils";
import { usePaginationContext } from "../../../../contexts/Pagination";

const GamesTable = React.memo(() => {
    const {
        games,
        sortColumn,
        sortOrder,
        setSortColumn,
        setSortOrder,
        site,
        umpirePay,
        fetchStatus,
    } = useGamesContext();
    const { page, itemsPerPage: gamesPerPage } = usePaginationContext();

    const paginatedGames = useMemo(() => {
        return games.slice(
            page * gamesPerPage - gamesPerPage,
            page * gamesPerPage,
        );
    }, [games, page, gamesPerPage]);

    const handleHeaderClick = (column: SortColumns) => {
        if (column !== sortColumn) {
            setSortColumn(column);
            setSortOrder("ascending");
        } else {
            setSortOrder(prev =>
                prev === "ascending" ? "descending" : "ascending",
            );
        }
    };

    return (
        <Table
            striped
            className={classNames({
                dataTable: true,
            })}
            style={{
                opacity: fetchStatus === "pending" ? 0.4 : 1,
            }}
        >
            <thead>
                <tr>
                    <th
                        onClick={() => handleHeaderClick("date")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "date" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "date" &&
                                sortOrder === "descending",
                        })}
                    >
                        Date
                    </th>
                    <th
                        onClick={() => handleHeaderClick("age")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "age" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "age" &&
                                sortOrder === "descending",
                        })}
                    >
                        Age
                    </th>
                    <th data-sortable="false">
                        {site === 1 ? "League" : "Tournament"}
                    </th>
                    <th
                        onClick={() => handleHeaderClick("location")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "location" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "location" &&
                                sortOrder === "descending",
                        })}
                    >
                        Location
                    </th>
                    <th data-sortable="false">Plate Umpire</th>
                    <th data-sortable="false">Field Umpire</th>
                    <th data-sortable="false">Pay 1</th>
                    <th data-sortable="false">Pay 2</th>
                    <th
                        data-sortable="false"
                        colSpan={2}
                        className={classNames("text-center")}
                    >
                        Logs
                    </th>
                </tr>
            </thead>
            <tbody>
                {paginatedGames.map(g => {
                    const ump1Pay = umpirePay
                        .find(u => u.ageLevel === g.ageLevel.ageLevel)
                        ?.umpires.find(
                            u =>
                                u.title === "Umpire" ||
                                u.title === "Plate Umpire",
                        )?.pay;
                    const ump2Pay = umpirePay
                        .find(u => u.ageLevel === g.ageLevel.ageLevel)
                        ?.umpires.find(u => u.title === "Field Umpire")?.pay;

                    return (
                        <tr key={g.id}>
                            <td style={{ width: "10%" }}>
                                {dateFormat.format(new Date(g.date))}
                                <br />@{" "}
                                {defaultTimeFormat.format(new Date(g.date))}
                            </td>
                            <td style={{ width: "5%" }}>
                                {g.ageLevel.ageLevel}
                            </td>
                            <td style={{ width: "20%" }}>
                                <b>{g.leagueName}</b>
                                <br />
                                {`${g.visitor.name || "TBD"} @ ${g.home.name || "TBD"}`}
                            </td>
                            <td style={{ width: "15%" }}>
                                <Link
                                    to={`https://www.majorybl.com/fields/${g.fieldId}`}
                                    target="_blank"
                                >
                                    {g.fieldName}
                                </Link>
                            </td>
                            <td style={{ width: "15%" }}>
                                {
                                    g.umpires?.find(u => u.location === "plate")
                                        ?.name
                                }
                            </td>
                            <td style={{ width: "15%" }}>
                                {
                                    g.umpires?.find(u => u.location === "field")
                                        ?.name
                                }
                            </td>
                            <td style={{ width: "5%" }}>
                                {ump1Pay &&
                                    currencyFormat.format(parseInt(ump1Pay))}
                            </td>
                            <td style={{ width: "5%" }}>
                                {ump2Pay &&
                                    currencyFormat.format(parseInt(ump2Pay))}
                            </td>
                            <td style={{ width: "5%" }}>
                                <Link
                                    to={`/admin/game-logs/${g.id}`}
                                    target="_blank"
                                >
                                    Game Logs
                                </Link>
                            </td>
                            <td>
                                {g.report && (
                                    <Link
                                        to={`/admin/ejection-reports/${g.id}`}
                                        target="_blank"
                                    >
                                        Ejection Report
                                    </Link>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr>
                    <th>Date</th>
                    <th>Age</th>
                    <th>{site === 1 ? "League" : "Tournament"}</th>
                    <th>Location</th>
                    <th>Plate Umpire</th>
                    <th>Field Umpire</th>
                    <th>Pay 1</th>
                    <th>Pay 2</th>
                    <th colSpan={2} className={classNames("text-center")}>
                        Logs
                    </th>
                </tr>
            </tfoot>
        </Table>
    );
});

export default GamesTable;
